import React, {
  Suspense,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import qs from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import createStyles from '@guestyci/foundation/createStyles';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import utils from 'utils';
import constants from 'constant';
import qParams from 'app/routes/queryParamsVariables';
import config from 'config';
import IframePlaceholder from 'app/module/micro-frontend/IframePlaceholder';

const useStyles = createStyles(
  (theme: typeof defaultTheme) => ({
    root: {
      border: 0,
      width: '100%',
      height: '100%',
    },

    title: {
      padding: '0 !important',
      backgroundColor: theme.palette.secondary.background,
    },
    titleWrapper: {
      maxWidth: '1024px',
    },
  }),
  { name: 'Iframe' }
);

let isFirstLoad = false;

const getContentWindow = (): Window => {
  const iframe = document.querySelector(
    '#layout-page-iframe'
  ) as HTMLIFrameElement;
  return iframe.contentWindow!;
};

const callReactAppLoaded = () => {
  if (!isFirstLoad) {
    isFirstLoad = true;
    getContentWindow().postMessage({ call: 'reactAppLoaded' }, '*');
  }
};

const postAuthDataToYPA = () => {
  const localstorageData = localStorage.getItem(
    constants.LOCAL_STORAGE_KEY_NAME
  );

  getContentWindow().postMessage(
    { call: constants.messageEvents.setAuthData, value: localstorageData },
    '*'
  );
};

function createIframeSrcWithSessionToken(src = '', providedQueryParams = {}) {
  const { sessionToken, authenticatedData } = utils.getLocalStorageByKey(
    'sessionToken',
    'authenticatedData'
  );
  const {
    userId,
    inTrial,
    isOnboardingUser,
    subscribed,
    firstName,
    lastName,
    username,
    trialEndDate,
    dayOfTrial,
    accountId,
    country,
  } = authenticatedData.baseUserInfo;

  const dateTimeFormat = authenticatedData.dateTimeFormat;

  const queryParams = qs.stringify({
    [qParams.sessionToken]: sessionToken,
    [qParams.userId]: userId,
    inTrial,
    isOnboardingUser,
    subscribed,
    firstName,
    lastName,
    username,
    trialEndDate,
    dayOfTrial,
    accountId,
    country,
    dateTimeFormat,
    ...providedQueryParams,
  });

  return `${config.iframeOrigin.replace('/#', `?${queryParams}#`)}${src}`;
}

const IFrameImplementation = React.forwardRef(
  (_, ref: React.ForwardedRef<HTMLIFrameElement>) => {
    const history = useHistory();
    const location = useLocation<Location>();
    const classes = useStyles();
    const layoutQueryParams = useMemo(() => qs.parse(location.search), []);
    const src = createIframeSrcWithSessionToken(location.pathname, {
      // @ts-ignore
      ...history.location.state?.queryParams,
      ...layoutQueryParams,
    });
    const awaiter = useRef<{
      promise?: null | Promise<void>;
      resolve: () => void;
      reject: () => void;
    }>(null);
    const [isLoaded, triggerLoad] = useState(false);

    if (awaiter.current?.promise) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw awaiter.current.promise;
    }

    useLayoutEffect(() => {
      if (awaiter.current === null) {
        // @ts-ignore
        awaiter.current = {};
        awaiter.current!.promise = new Promise((resolve, reject) => {
          Object.assign(awaiter.current!, { resolve, reject });
        });
        triggerLoad(true);
      }
    }, [isLoaded]);

    return (
      <iframe
        ref={ref}
        src={src}
        className={classes.root}
        id="layout-page-iframe"
        title="layout-page-iframe"
        onLoad={() => {
          awaiter.current!.promise = null;
          awaiter.current?.resolve();
          postAuthDataToYPA();
          callReactAppLoaded();
        }}
        onError={() => {
          awaiter.current!.promise = null;
          awaiter.current?.reject();
        }}
      />
    );
  }
);

export default function IFrame(props: any) {
  return (
    <Suspense fallback={<IframePlaceholder />}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <IFrameImplementation {...props} />
    </Suspense>
  );
}
